/* eslint-disable @typescript-eslint/no-explicit-any */
import { FC, useEffect, useState, useMemo, useCallback } from 'react';
// import { loadIntercom } from 'next-intercom';
import { useSearchParams } from 'react-router-dom';

import 'rc-time-picker/assets/index.css';
import { isEmpty, cloneDeep, debounce } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import selectors from 'store/selectors';
import actions from 'store/actions';
import { getEditDetailFormatData } from 'utils/globalFunction';
import { initDataCreateTask } from 'utils/globalVariable';

import { useReactRouter } from 'controller/useReactRouter';
import { TaskContentCnt, TaskListWrapper } from 'pages/taskDashboard.style';
import TaskListView from 'pages/task/TaskListView/TaskListview.cmp';
import TaskCalendar from 'pages/task/TaskListView/TaskCalendar/TaskCalendar.view';
import { Route, Routes } from 'react-router-dom';
import withTitleHoc from 'components/Routes/withTitleHoc';
import { PAGE_TITLES } from 'utils/globalVariable';

import { useWorkspaceHook } from 'utils/CustomHooks/useWorkspaceHook';
import useTaskList from 'utils/CustomHooks/useTasksList';
import SavedTaskTemplates from './savedtasktemplates';

const TaskCalendarCmp = withTitleHoc(TaskCalendar);
const TaskListViewCmp = withTitleHoc(TaskListView);
const SavedTaskTemplatesCmp = withTitleHoc(SavedTaskTemplates);

import TaskListHeader from 'pages/task/TaskListView/TaskListHeader/TaskListHeader';
import TaskCalendarHeader from 'pages/task/TaskListView/TaskCalendarHeader/TaskCalendarHeader';
import { useTaskEdit } from 'controller/useTaskEdit';
import xeniaApi from 'api/index';
import { setDialog } from 'store/actions/dialogActions';
import useTaskFilters from 'utils/CustomHooks/useTaskFilters/useTaskFilters';
import { useCustomPermission } from 'utils/CustomHooks/useCustomPermission';
import { dateRangePickerSideMenuCommon } from 'utils/datePicker';
import { getUserHotelTz } from 'store/selectors/auth';
import { comparator } from './task/TaskListView/constants';

export const filtersKeys = [
  'taskStatus',
  'priority',
  'taskLocations',
  'taskAssets',
  'taskCategories',
  'taskAssignees',
  'taskDueFilter',
  'taskType',
];
const TaskHome: FC = () => {
  const dispatch = useDispatch();
  const { hasPermission, PERMISSIONS } = useCustomPermission();
  const [searchParams] = useSearchParams();

  const tz = useSelector(getUserHotelTz);

  const default90DaysFilter = useMemo(() => {
    if (tz) {
      const range = dateRangePickerSideMenuCommon(tz)[8];
      return {
        from: range?.range?.from,
        to: range?.range?.to,
        selectedDateOption: range?.label,
      };
    }
    return {};
  }, [tz]);

  const [forcedAdvanceFilters, setForcedAdvanceFilters] = useState<any>(null);
  const [dateFilter, setDateFilter] = useState<any>(default90DaysFilter);

  const {
    handleSetAdvanceFiltersCommon,
    fetchAdvanceFilters,
    syncAdvanceFilters,
  } = useTaskFilters();

  const disabledAdvanceFilters = useMemo(() => {
    if (forcedAdvanceFilters) {
      const disabledFilters = forcedAdvanceFilters.map((f) => f.filterName);
      return disabledFilters;
    }
    return null;
  }, [forcedAdvanceFilters, searchParams]);

  const formatQueryFilters = (advanceFilters) => {
    let allFilters: any[] = [];
    if (advanceFilters) {
      const filtersSeparated = advanceFilters.split('.') as string[];
      allFilters = filtersSeparated.map((filterString) => {
        const filter = filterString.split('|');
        const filterName = filter[0];
        const filterComparator = filter[1];
        const filterValue = filter[2];
        const filterObj = {
          filterName,
          comparator: filterComparator,
          value: filterValue.split(','),
          conditional: 'and',
        };
        return filterObj;
      });
    }
    if (allFilters.length > 0)
      return {
        condition: 'AND',
        filters: allFilters,
      };
    return undefined;
  };

  const formatDateFilter = (dateFilter) => {
    return {
      comparator: 'is',
      conditional: 'and',
      filterName: 'createdDate',
      value: {
        from: dateFilter.from,
        to: dateFilter.to,
      },
    };
  };

  const debouncedGetTasks = useCallback(
    debounce(async (searchParams, dateFilter) => {
      dispatch(actions.setLoader({ type: 'tasksList', value: true }));
      dispatch(actions.setLoader({ type: 'tasksCalendar', value: true }));
      const dbFilters = await fetchAdvanceFilters();
      syncAdvanceFilters(dbFilters);
      if (searchParams.get('filters')) {
        const filters = formatQueryFilters(searchParams.get('filters'));
        if (filters) {
          setForcedAdvanceFilters(filters.filters);
          handleSetAdvanceFiltersCommon(
            null,
            [...filters.filters, formatDateFilter(dateFilter)],
            {
              skipFilterSave: true,
            },
          );
        }
      } else if (dbFilters?.filters) {
        setForcedAdvanceFilters(null);
        handleSetAdvanceFiltersCommon(
          dbFilters,
          [formatDateFilter(dateFilter)],
          {
            skipFilterSave: true,
          },
        );
      } else {
        setForcedAdvanceFilters(null);
        handleSetAdvanceFiltersCommon(null, [formatDateFilter(dateFilter)], {
          skipFilterSave: true,
        });
      }
    }, 500),
    [],
  );

  useEffect(() => {
    if (!hasPermission(PERMISSIONS.CAN_VIEW_TASKS)) {
      setTimeout(() => {
        navigateWithWorkspaceUrl('/dashboard');
      }, 1000);
    }
  }, []);

  useEffect(() => {
    debouncedGetTasks(searchParams, dateFilter);
  }, [searchParams, dateFilter]);

  const { isCalendarView } = useTaskList();

  const { handleEditDetailTask } = useTaskEdit();

  const getTaskDetailIdFromNotification: any = useSelector(
    selectors.getTaskDetailIdFromNotification,
  );

  const loaders = useSelector(selectors.getLoaders);

  const getSearchText: any = useSelector(selectors.getSearchTextFromStore);
  const taskListFilters: any = useSelector(selectors.getTaskListFilters);
  const currentTask = useSelector(selectors.getCurrentTask);

  const { navigateWithWorkspaceUrl } = useWorkspaceHook();

  const { handleGetQueryParam } = useReactRouter();
  const { handleGetPathName } = useReactRouter();

  const path = handleGetPathName();

  const [taskCreateFormData, SetTaskCreateFormData] = useState(
    cloneDeep(initDataCreateTask),
  );
  const [showTaskIntervalButtons, setShowTaskIntervalButtons] =
    useState<boolean>(true);

  const setTaskIntervalButtonsInitialState = () => {
    const hasDeadlineOrCadence =
      taskCreateFormData?.recurringTask === true ||
      !isEmpty(taskCreateFormData?.dueTime) ||
      !isEmpty(taskCreateFormData?.startTime) ||
      !showTaskIntervalButtons;
    setShowTaskIntervalButtons(!hasDeadlineOrCadence);
  };

  //load intercom
  // loadIntercom({
  //   appId: 'kx0bab1r', // default : ''
  //   initWindow: false, // default: true
  //   delay: 0, // default: 0  - usefull for mobile devices to prevent blocking the main thread
  // });

  useEffect(() => {
    setTaskIntervalButtonsInitialState();
  }, [taskCreateFormData]);

  useEffect(() => {
    const params = handleGetQueryParam();
    const channelId = params.get('channelId');
    const isCommentChannel =
      channelId && channelId.startsWith('task_comments_');
    if (channelId && !isCommentChannel) {
      navigateWithWorkspaceUrl(`/messages/${channelId}`);
      return;
    }
    const taskId =
      (isCommentChannel && channelId.substr(14)) || params.get('taskId');
    if (taskId) {
      const isCommentParam =
        params.get('comments') && params.get('comments') === '1';
      const section =
        isCommentChannel || isCommentParam ? 'comments' : 'detail';
      handleEditDetailTask(taskId, section);
      navigateWithWorkspaceUrl('/tasks/list');
    }
  }, [handleGetQueryParam()]);

  useEffect(() => {
    if (getTaskDetailIdFromNotification !== '') {
      handleEditDetailTask(getTaskDetailIdFromNotification, 'detail');
    }
  }, [getTaskDetailIdFromNotification]);

  useEffect(() => {
    if (currentTask) {
      prepareAddEditTaskFormData(currentTask);
    }
  }, [currentTask]);

  const setTaskApiFilters = (obj) => {
    const filters = {
      ...taskListFilters,
      ...obj,
    };
    dispatch(actions.setTaskListFilters(filters));
  };

  const prepareAddEditTaskFormData = (task) => {
    const editDetailFormattedData = getEditDetailFormatData(
      task,
      initDataCreateTask,
    );
    SetTaskCreateFormData(editDetailFormattedData);
  };

  const fetchTask = async (id, isRecur?: any) => {
    if (!isRecur) {
      const taskData = await xeniaApi.taskDetailSingleListing({
        id,
      });
      dispatch(
        actions.editSingleTask({
          taskId: id,
          task: { ...taskData?.data },
        }),
      );
    } else {
      closeTaskDetail();
    }
  };

  const closeTaskDetail = () => {
    dispatch(
      setDialog({
        open: false,
        dialogId: 'taskDetail',
      }),
    );
  };

  const showTasksListFilters = !loaders?.filters?.isLoading;
  return hasPermission(PERMISSIONS.CAN_VIEW_TASKS) ? (
    <TaskContentCnt>
      {showTasksListFilters && (
        <>
          {path?.includes('/tasks/list') && (
            <TaskListHeader
              forcedAdvanceFilters={forcedAdvanceFilters}
              disabledAdvanceFilters={disabledAdvanceFilters}
              isMainView={!searchParams.get('filters')}
              dateFilter={dateFilter}
              setDateFilter={setDateFilter}
              formatDateFilter={formatDateFilter}
            />
          )}{' '}
        </>
      )}
      {isCalendarView() && (
        <TaskCalendarHeader
          forcedAdvanceFilters={forcedAdvanceFilters}
          disabledAdvanceFilters={disabledAdvanceFilters}
          isMainView={!searchParams.get('filters')}
        />
      )}

      <div style={{ position: 'relative', display: 'flex' }}>
        <Routes>
          <Route
            path="calendar"
            element={
              <TaskCalendarCmp
                handleEditDetailTask={handleEditDetailTask}
                title={PAGE_TITLES.task}
                childTitle={PAGE_TITLES.taskCalendar}
                forcedAdvanceFilters={forcedAdvanceFilters}
              />
            }
          />

          {hasPermission(PERMISSIONS.CAN_MANAGE_TASKS) && (
            <Route
              path="savedtasktemplates"
              element={
                <SavedTaskTemplatesCmp
                  title={PAGE_TITLES.task}
                  childTitle={PAGE_TITLES.taskTemplates}
                />
              }
            />
          )}

          <Route
            path="list"
            element={
              <TaskListWrapper>
                <TaskListViewCmp
                  title={PAGE_TITLES.task}
                  childTitle={PAGE_TITLES.taskList}
                  /*start : block of code needs to be fixed later once tasks loading strategy will be fixed */
                  setTaskApiFilters={setTaskApiFilters}
                  taskListFilters={taskListFilters}
                  getSearchText={getSearchText}
                  /*end: block of code needs to be fixed later once tasks loading strategy will be fixed */
                  onRowClick={handleEditDetailTask}
                  //task action dropdown props will be removed once task details is revamped
                  taskActionDropdownProps={{
                    successCB: fetchTask,
                  }}
                  forcedAdvanceFilters={forcedAdvanceFilters}
                  isMainView={!searchParams.get('filters')}
                />
              </TaskListWrapper>
            }
          />
        </Routes>
      </div>
    </TaskContentCnt>
  ) : null;
};

export default TaskHome;
